import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  RoomService as VenueIcon
} from '@mui/icons-material';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import PageHeader from '../common/PageHeader';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import { db, functions } from '../../firebase/config';
import { selectCanManageGlobalSettings, selectCurrentUser } from '../../redux/slices/authSlice';

/**
 * Einrichtungs-Assistent für den Venue-Bestellkanal (Taxi Butler).
 * Konzept: docs/general/venue_bestellknopf_taxibutler_konzept.md (§10.7)
 *
 * Besonderheit gegenüber den anderen Assistenten: Es gibt keinen Verbindungstest,
 * den wir auslösen könnten. Taxi Butler ruft unsere Endpunkte an, nicht umgekehrt —
 * Schritt 5 zeigt deshalb, was tatsächlich bei uns angekommen ist.
 */
const TOTAL_STEPS = 6;

const DEVICE_OPTIONS = [
  {
    value: 'one',
    label: 'Butler ONE',
    price: '69 € einmalig + 14 €/Monat (24 Monate) bzw. 17 €/Monat monatlich kündbar',
    detail: 'Ein Knopf, 4G. Bestellung ohne Ziel- oder Zeitangabe.'
  },
  {
    value: 'plus6',
    label: 'Butler PLUS 6',
    price: '150 € einmalig + 18 €/Monat (24 Monate) bzw. 21 €/Monat monatlich kündbar',
    detail: 'Touchscreen, 4G + WLAN. Ziel, Zeit, Fahrzeugart, Fahrgastdaten, Ticketdruck.'
  },
  {
    value: 'kiosk',
    label: 'KIOSK',
    price: '950 € Kaufpreis + 25 €/Monat (24 Monate) bzw. 27 €/Monat kurze Laufzeit',
    detail: 'Standgerät für Empfangsbereiche, gleiche Funktionen wie PLUS.'
  }
];

const VEHICLE_TYPE_OPTIONS = [
  { value: 1, label: 'Standard-Taxi' },
  { value: 2, label: 'Großraum' },
  { value: 3, label: 'Kleinbus (7 Sitze)' },
  { value: 4, label: 'Kleinbus (8 Sitze)' }
];

const emptyVenueForm = {
  id: null,
  // Vorhandene Kennung aus dem Taxi-Butler-Portal. Leer lassen erzeugt eine neue.
  customId: '',
  name: '',
  address: '',
  latitude: null,
  longitude: null,
  phone: '',
  operatorId: '',
  driverNote: '',
  defaultVehicleType: 1,
  butlerSerialNos: '',
  carTypeRows: [{ identifier: '', vehicleType: 1 }],
  active: true
};

const VenueChannelSetupGuidePage = () => {
  const navigate = useNavigate();
  const canManage = useSelector(selectCanManageGlobalSettings);
  // Die Regeln geben Venues nur im eigenen Mandanten frei — die ID muss deshalb
  // vom angemeldeten Konto kommen und darf nicht fest verdrahtet sein.
  const currentUser = useSelector(selectCurrentUser);
  const organizationId = currentUser?.organizationId || 'default_organization';

  const [activeStep, setActiveStep] = useState(0);
  const [infoConfirmed, setInfoConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Schritt 2 — Flottenanmeldung bzw. Umstellung vorhandener Geräte
  const [fleetMode, setFleetMode] = useState('new');
  const [deviceType, setDeviceType] = useState('plus6');
  const [venueCount, setVenueCount] = useState(1);
  const [fleetNote, setFleetNote] = useState('');
  const [tbCompanyName, setTbCompanyName] = useState('');
  const [tbCustomerNumber, setTbCustomerNumber] = useState('');
  const [onboardingStatus, setOnboardingStatus] = useState('');
  const [fleetRequestedAt, setFleetRequestedAt] = useState(null);

  // Schritt 3 — Zugangsdaten
  const [channelEnabled, setChannelEnabled] = useState(false);
  const [freshCredentials, setFreshCredentials] = useState(null);

  // Schritt 4 — Venues
  const [venues, setVenues] = useState([]);
  const [operators, setOperators] = useState([]);
  const [venueDialogOpen, setVenueDialogOpen] = useState(false);
  const [venueForm, setVenueForm] = useState(emptyVenueForm);

  // Schritt 5 — Verbindungstest
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'appSettings', 'venueChannel'),
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        setChannelEnabled(data.enabled === true);
        setOnboardingStatus(data.onboardingStatus || '');
        if (data.fleetRequest) {
          setFleetMode(data.fleetRequest.mode === 'existing' ? 'existing' : 'new');
          setDeviceType(data.fleetRequest.deviceType || 'plus6');
          setVenueCount(data.fleetRequest.venueCount || 1);
          setFleetNote(data.fleetRequest.note || '');
          setTbCompanyName(data.fleetRequest.tbCompanyName || '');
          setTbCustomerNumber(data.fleetRequest.tbCustomerNumber || '');
          setFleetRequestedAt(data.fleetRequest.requestedAt?.toDate?.() || null);
        }
      },
      () => undefined
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'venues'),
      (snapshot) => setVenues(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => undefined
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'operators'), where('active', '==', true)),
      (snapshot) => setOperators(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => undefined
    );
    return unsubscribe;
  }, []);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const callable = httpsCallable(functions, 'getVenueChannelStatus');
      const result = await callable({});
      setStatus(result.data);
    } catch (err) {
      setError(err?.message || 'Status konnte nicht geladen werden.');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeStep >= 2) loadStatus();
  }, [activeStep, loadStatus]);

  const hasCredentials = useMemo(
    () => Boolean(status?.accounts?.some((a) => a.enabled)),
    [status]
  );

  const completedCount = useMemo(() => {
    let count = 0;
    if (infoConfirmed) count += 1;
    if (fleetRequestedAt) count += 1;
    if (hasCredentials) count += 1;
    if (venues.length > 0) count += 1;
    if (status?.recentRequests?.length > 0) count += 1;
    if (channelEnabled) count += 1;
    return Math.min(count, TOTAL_STEPS);
  }, [infoConfirmed, fleetRequestedAt, hasCredentials, venues.length, status, channelEnabled]);

  const runCallable = async (name, payload) => {
    setBusy(true);
    setError(null);
    try {
      const callable = httpsCallable(functions, name);
      const result = await callable(payload);
      return result.data;
    } catch (err) {
      setError(err?.message || 'Die Aktion ist fehlgeschlagen. Bitte erneut versuchen.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleFleetRequest = async () => {
    const result = await runCallable('configureVenueChannel', {
      fleetRequest: {
        mode: fleetMode,
        deviceType,
        venueCount: Number(venueCount) || 1,
        note: fleetNote,
        tbCompanyName,
        tbCustomerNumber
      }
    });
    if (result) setActiveStep(2);
  };

  const handleCreateCredentials = async () => {
    const result = await runCallable('createVenueApiCredentials', { label: 'Taxi Butler' });
    if (result) {
      setFreshCredentials(result);
      loadStatus();
    }
  };

  const handleRevoke = async (accountId) => {
    const result = await runCallable('revokeVenueApiCredentials', { accountId });
    if (result) {
      setFreshCredentials(null);
      loadStatus();
    }
  };

  const handleToggleChannel = async (enabled) => {
    await runCallable('configureVenueChannel', { enabled });
  };

  const openVenueDialog = (venue = null) => {
    if (venue) {
      const carTypeRows = Object.entries(venue.carTypeMap || {}).map(([identifier, vehicleType]) => ({
        identifier,
        vehicleType: Number(vehicleType) || 1
      }));
      setVenueForm({
        id: venue.id,
        customId: '',
        name: venue.name || '',
        address: venue.address || '',
        latitude: venue.latitude ?? null,
        longitude: venue.longitude ?? null,
        phone: venue.phone || '',
        operatorId: venue.operatorId || '',
        driverNote: venue.driverNote || '',
        defaultVehicleType: Number(venue.defaultVehicleType) || 1,
        butlerSerialNos: (venue.butlerSerialNos || []).join(', '),
        carTypeRows: carTypeRows.length ? carTypeRows : [{ identifier: '', vehicleType: 1 }],
        active: venue.active !== false
      });
    } else {
      setVenueForm(emptyVenueForm);
    }
    setVenueDialogOpen(true);
  };

  const handleSaveVenue = async () => {
    setBusy(true);
    setError(null);
    try {
      const carTypeMap = {};
      venueForm.carTypeRows.forEach((row) => {
        const key = row.identifier.trim().toLowerCase();
        if (key) carTypeMap[key] = Number(row.vehicleType) || 1;
      });
      const payload = {
        name: venueForm.name.trim(),
        address: venueForm.address.trim(),
        latitude: venueForm.latitude,
        longitude: venueForm.longitude,
        phone: venueForm.phone.trim() || null,
        operatorId: venueForm.operatorId || null,
        driverNote: venueForm.driverNote.trim() || null,
        defaultVehicleType: Number(venueForm.defaultVehicleType) || 1,
        butlerSerialNos: venueForm.butlerSerialNos
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        carTypeMap,
        active: venueForm.active,
        organizationId
      };
      if (venueForm.id) {
        await updateDoc(doc(db, 'venues', venueForm.id), payload);
      } else if (venueForm.customId.trim()) {
        // Bestehende Taxi-Butler-Kunden übernehmen ihre vorhandene
        // customerReference als Dokument-ID — dann muss an den Geräten nichts
        // umkonfiguriert werden.
        const customId = venueForm.customId.trim();
        if (venues.some((v) => v.id === customId)) {
          throw new Error(`Die Kennung "${customId}" ist bereits vergeben.`);
        }
        await setDoc(doc(db, 'venues', customId), payload);
      } else {
        await addDoc(collection(db, 'venues'), payload);
      }
      setVenueDialogOpen(false);
    } catch (err) {
      setError(err?.message || 'Venue konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteVenue = async (venueId) => {
    try {
      await deleteDoc(doc(db, 'venues', venueId));
    } catch (err) {
      setError(err?.message || 'Venue konnte nicht gelöscht werden.');
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(text);

  if (!canManage) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <PageHeader
          title="Bestellknopf einrichten"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Einstellungen', path: '/einstellungen' },
            { label: 'Bestellknopf einrichten' }
          ]}
        />
        <Alert severity="info">
          Der Venue-Bestellkanal wird von der Hauptzentrale verwaltet. Bitte wenden
          Sie sich an Ihre Zentrale.
        </Alert>
      </Container>
    );
  }

  const navigation = (canContinue = true) => (
    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
      <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)} disabled={!canContinue}>
        Weiter
      </Button>
      {activeStep > 0 && <Button onClick={() => setActiveStep((s) => s - 1)}>Zurück</Button>}
    </Stack>
  );

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <PageHeader
        title="Bestellknopf einrichten"
        description="Bestellgeräte in Hotels, Restaurants und Kliniken an Ihre Disposition anbinden"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Einstellungen', path: '/einstellungen' },
          { label: 'Bestellknopf einrichten' }
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <VenueIcon color="primary" />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {completedCount} von {TOTAL_STEPS} Schritten erledigt
            </Typography>
            {channelEnabled && (
              <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Aktiv" />
            )}
          </Stack>
          <LinearProgress variant="determinate" value={(completedCount / TOTAL_STEPS) * 100} />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {/* 1 — Info */}
          <Step>
            <StepLabel onClick={() => setActiveStep(0)} sx={{ cursor: 'pointer' }}>
              Was ist ein Bestellknopf?
            </StepLabel>
            <StepContent>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Ein kleines Gerät am Tresen: Das Personal im Hotel, Restaurant oder in
                der Klinik drückt einen Knopf, und die Bestellung landet direkt in Ihrer
                Disposition — ohne Anruf. Die Geräte stammen von Taxi Butler; den Vertrag
                schließen Sie direkt mit Taxi Butler, über NextCab entstehen keine
                Zusatzkosten. Der Testzeitraum beträgt vier Wochen.
              </Typography>
              <Table size="small" sx={{ mb: 1.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Gerät</TableCell>
                    <TableCell>Preis</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {DEVICE_OPTIONS.map((option) => (
                    <TableRow key={option.value}>
                      <TableCell>
                        <strong>{option.label}</strong>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {option.detail}
                        </Typography>
                      </TableCell>
                      <TableCell>{option.price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary">
                Preise zzgl. MwSt. und Versand. Enthalten sind SIM-Karte, Support,
                Einweisung und ein Jahr Garantie mit Sofortaustausch.
              </Typography>
              <FormControlLabel
                sx={{ display: 'block', mt: 1 }}
                control={
                  <Checkbox
                    checked={infoConfirmed}
                    onChange={(e) => setInfoConfirmed(e.target.checked)}
                  />
                }
                label="Verstanden — weiter zur Anmeldung"
              />
              {navigation(infoConfirmed)}
            </StepContent>
          </Step>

          {/* 2 — Flotte anmelden */}
          <Step>
            <StepLabel onClick={() => setActiveStep(1)} sx={{ cursor: 'pointer' }}>
              Geräte anfragen oder vorhandene umstellen
            </StepLabel>
            <StepContent>
              <RadioGroup
                value={fleetMode}
                onChange={(e) => setFleetMode(e.target.value)}
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label="Wir brauchen neue Geräte"
                />
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  label="Wir nutzen Taxi Butler bereits"
                />
              </RadioGroup>

              {fleetMode === 'new' ? (
                <>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Sagen Sie uns, wie viele Bestellorte Sie ausstatten möchten. Wir
                    melden Sie bei Taxi Butler an und kümmern uns um den Rest.
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      select
                      label="Gerätetyp"
                      value={deviceType}
                      onChange={(e) => setDeviceType(e.target.value)}
                      size="small"
                      fullWidth
                    >
                      {DEVICE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Anzahl Bestellorte"
                      type="number"
                      value={venueCount}
                      onChange={(e) => setVenueCount(e.target.value)}
                      size="small"
                      fullWidth
                      inputProps={{ min: 1, max: 999 }}
                    />
                  </Stack>
                </>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Ihre Geräte und Ihr Vertrag bleiben, wie sie sind. Umgestellt wird
                    nur, wohin Taxi Butler die Bestellungen schickt — bisher an Ihr
                    altes System, künftig an NextCab. Das macht Taxi Butler für Sie;
                    wir übermitteln die nötigen Angaben.
                  </Alert>
                  <Stack spacing={2}>
                    <TextField
                      label="Firmenname bei Taxi Butler"
                      value={tbCompanyName}
                      onChange={(e) => setTbCompanyName(e.target.value)}
                      size="small"
                      fullWidth
                      helperText="So, wie Ihr Unternehmen im Taxi-Butler-Portal heißt"
                    />
                    <TextField
                      label="Kundennummer bei Taxi Butler (falls bekannt)"
                      value={tbCustomerNumber}
                      onChange={(e) => setTbCustomerNumber(e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Anzahl vorhandener Bestellorte"
                      type="number"
                      value={venueCount}
                      onChange={(e) => setVenueCount(e.target.value)}
                      size="small"
                      fullWidth
                      inputProps={{ min: 1, max: 999 }}
                    />
                  </Stack>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>Wichtig für Schritt 4:</strong> Ihre Bestellorte sind bei
                    Taxi Butler bereits angelegt. Übernehmen Sie dort die vorhandene
                    Kennung („customerReference") — dann muss an Ihren Geräten nichts
                    umkonfiguriert werden. Sie finden sie im Taxi-Butler-Portal beim
                    jeweiligen Bestellort; falls dort keine hinterlegt ist, ordnen wir
                    über die Seriennummer des Geräts zu.
                  </Alert>
                </>
              )}

              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField
                  label="Anmerkung (optional)"
                  value={fleetNote}
                  onChange={(e) => setFleetNote(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder={
                    fleetMode === 'new'
                      ? 'Zum Beispiel: zwei Hotels und ein Restaurant in der Innenstadt'
                      : 'Zum Beispiel: bisher angebunden über taxi.de'
                  }
                />
                {fleetRequestedAt && (
                  <Alert severity="success">
                    Anfrage übermittelt am {fleetRequestedAt.toLocaleDateString('de-DE')}.
                    {onboardingStatus ? ` Status: ${onboardingStatus}` : ' Wir melden uns bei Ihnen.'}
                  </Alert>
                )}
                <Box>
                  <Button variant="contained" onClick={handleFleetRequest} disabled={busy}>
                    {busy ? <CircularProgress size={20} /> : 'Anfrage senden'}
                  </Button>
                </Box>
              </Stack>
              {navigation(true)}
            </StepContent>
          </Step>

          {/* 3 — Technische Kopplung */}
          <Step>
            <StepLabel onClick={() => setActiveStep(2)} sx={{ cursor: 'pointer' }}>
              Zugangsdaten erzeugen
            </StepLabel>
            <StepContent>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Taxi Butler braucht eine Adresse und ein Passwort, um Bestellungen an
                Ihre Disposition zu übergeben. Beides erzeugen wir hier — die Angaben
                geben wir für Sie an Taxi Butler weiter.
              </Typography>

              {freshCredentials && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Das Passwort wird nur dieses eine Mal angezeigt.
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      ['Adresse', freshCredentials.baseUrl],
                      ['Benutzername', freshCredentials.accountId],
                      ['Passwort', freshCredentials.secret]
                    ].map(([label, value]) => (
                      <Stack key={label} direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" sx={{ minWidth: 110 }}>
                          {label}
                        </Typography>
                        <TextField
                          value={value}
                          size="small"
                          fullWidth
                          InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                        />
                        <Tooltip title="Kopieren">
                          <IconButton size="small" onClick={() => copy(value)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Stack>
                </Alert>
              )}

              {status?.accounts?.length > 0 && (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Benutzername</TableCell>
                      <TableCell>Erstellt</TableCell>
                      <TableCell>Letzter Zugriff</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {status.accounts.map((account) => (
                      <TableRow key={account.accountId}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {account.accountId}
                          {!account.enabled && (
                            <Chip size="small" label="zurückgezogen" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {account.createdAt
                            ? new Date(account.createdAt).toLocaleDateString('de-DE')
                            : '–'}
                        </TableCell>
                        <TableCell>
                          {account.lastRequestAt
                            ? new Date(account.lastRequestAt).toLocaleString('de-DE')
                            : 'noch keiner'}
                        </TableCell>
                        <TableCell align="right">
                          {account.enabled && (
                            <Button size="small" color="error" onClick={() => handleRevoke(account.accountId)}>
                              Zurückziehen
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Button variant="contained" onClick={handleCreateCredentials} disabled={busy}>
                {busy ? <CircularProgress size={20} /> : 'Zugangsdaten erzeugen'}
              </Button>
              {navigation(hasCredentials)}
            </StepContent>
          </Step>

          {/* 4 — Venues */}
          <Step>
            <StepLabel onClick={() => setActiveStep(3)} sx={{ cursor: 'pointer' }}>
              Bestellorte anlegen
            </StepLabel>
            <StepContent>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Jedes Gerät gehört zu einem Bestellort. Die Adresse hier ist der
                Abholort — sie zählt für die Disposition, deshalb sollte sie genau
                stimmen. Die Kennung jedes Bestellorts tragen wir bei Taxi Butler als
                „customerReference" ein.
              </Typography>

              {venues.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Noch kein Bestellort angelegt.
                </Alert>
              ) : (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bestellort</TableCell>
                      <TableCell>Kennung</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {venues.map((venue) => (
                      <TableRow key={venue.id}>
                        <TableCell>
                          <strong>{venue.name}</strong>
                          {venue.active === false && (
                            <Chip size="small" label="inaktiv" sx={{ ml: 1 }} />
                          )}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {venue.address}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {venue.id}
                            </Typography>
                            <IconButton size="small" onClick={() => copy(venue.id)}>
                              <CopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openVenueDialog(venue)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteVenue(venue.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Button startIcon={<AddIcon />} variant="outlined" onClick={() => openVenueDialog()}>
                Bestellort hinzufügen
              </Button>
              {navigation(venues.length > 0)}
            </StepContent>
          </Step>

          {/* 5 — Verbindungstest */}
          <Step>
            <StepLabel onClick={() => setActiveStep(4)} sx={{ cursor: 'pointer' }}>
              Verbindung prüfen
            </StepLabel>
            <StepContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                Wir können die Verbindung nicht von uns aus anstoßen — Taxi Butler ruft
                uns an, nicht umgekehrt. Lösen Sie deshalb am Gerät eine Testbestellung
                aus; sie erscheint dann hier in der Liste.
              </Alert>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadStatus}
                  disabled={statusLoading}
                >
                  Aktualisieren
                </Button>
                {statusLoading && <CircularProgress size={16} />}
              </Stack>

              {status?.recentRequests?.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Zeitpunkt</TableCell>
                      <TableCell>Vorgang</TableCell>
                      <TableCell>Ergebnis</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {status.recentRequests.map((entry, index) => (
                      <TableRow key={`${entry.at}-${index}`}>
                        <TableCell>
                          {entry.at ? new Date(entry.at).toLocaleString('de-DE') : '–'}
                        </TableCell>
                        <TableCell>
                          {entry.method === 'POST' ? 'Bestellung' : 'Storno'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={entry.ok ? 'success' : 'error'}
                            label={entry.ok ? entry.detail || 'erfolgreich' : entry.detail || 'fehlgeschlagen'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Alert severity="warning">
                  Bisher ist keine Bestellung eingegangen.
                </Alert>
              )}
              {navigation(true)}
            </StepContent>
          </Step>

          {/* 6 — Aktiv */}
          <Step>
            <StepLabel onClick={() => setActiveStep(5)} sx={{ cursor: 'pointer' }}>
              Kanal aktivieren
            </StepLabel>
            <StepContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={channelEnabled}
                    onChange={(e) => handleToggleChannel(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Bestellungen von Venue-Geräten annehmen"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Eingehende Bestellungen erscheinen in der Fahrtverwaltung und auf der
                LiveMap mit dem Hinweis „Venue-Bestellung" und dem Namen des Bestellorts.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="contained" onClick={() => navigate('/einstellungen')}>
                  Fertig
                </Button>
                <Button onClick={() => setActiveStep((s) => s - 1)}>Zurück</Button>
              </Stack>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>

      {/* Bestellort anlegen/bearbeiten */}
      <Dialog open={venueDialogOpen} onClose={() => setVenueDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{venueForm.id ? 'Bestellort bearbeiten' : 'Bestellort hinzufügen'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name *"
              value={venueForm.name}
              onChange={(e) => setVenueForm((f) => ({ ...f, name: e.target.value }))}
              size="small"
              fullWidth
              placeholder="Hotel Stadtpark"
            />
            {!venueForm.id && (
              <TextField
                label="Vorhandene Kennung aus dem Taxi-Butler-Portal"
                value={venueForm.customId}
                onChange={(e) => setVenueForm((f) => ({ ...f, customId: e.target.value }))}
                size="small"
                fullWidth
                helperText={
                  'Nur ausfüllen, wenn der Bestellort bei Taxi Butler schon existiert — dann '
                  + 'muss am Gerät nichts geändert werden. Sonst leer lassen, wir vergeben eine.'
                }
              />
            )}
            <PlaceAutocomplete
              label="Abholadresse *"
              value={venueForm.address}
              onChange={(e) =>
                setVenueForm((f) => ({ ...f, address: e.target.value, latitude: null, longitude: null }))
              }
              onPlaceSelect={(address, location) =>
                setVenueForm((f) => ({
                  ...f,
                  address,
                  latitude: location?.latitude ?? null,
                  longitude: location?.longitude ?? null
                }))
              }
              required
              size="small"
              fullWidth
            />
            {venueForm.address && venueForm.latitude === null && (
              <Alert severity="warning">
                Bitte die Adresse aus den Vorschlägen auswählen — ohne Koordinaten kann
                der Bestellort nicht disponiert werden.
              </Alert>
            )}
            <TextField
              label="Telefon des Bestellorts"
              value={venueForm.phone}
              onChange={(e) => setVenueForm((f) => ({ ...f, phone: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              select
              label="Unternehmer"
              value={venueForm.operatorId}
              onChange={(e) => setVenueForm((f) => ({ ...f, operatorId: e.target.value }))}
              size="small"
              fullWidth
              helperText="Leer lassen, wenn die Zentrale disponiert"
            >
              <MenuItem value="">Zentrale</MenuItem>
              {operators.map((operator) => (
                <MenuItem key={operator.id} value={operator.id}>
                  {operator.name || operator.companyName || operator.id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Hinweis für den Fahrer"
              value={venueForm.driverNote}
              onChange={(e) => setVenueForm((f) => ({ ...f, driverNote: e.target.value }))}
              size="small"
              fullWidth
              placeholder="An der Rezeption klingeln"
            />
            <TextField
              select
              label="Fahrzeugtyp als Standard"
              value={venueForm.defaultVehicleType}
              onChange={(e) => setVenueForm((f) => ({ ...f, defaultVehicleType: e.target.value }))}
              size="small"
              fullWidth
            >
              {VEHICLE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Geräte-Seriennummern"
              value={venueForm.butlerSerialNos}
              onChange={(e) => setVenueForm((f) => ({ ...f, butlerSerialNos: e.target.value }))}
              size="small"
              fullWidth
              helperText="Mehrere durch Komma trennen — steht auf der Rückseite des Geräts"
            />

            <Divider />
            <Box>
              <Typography variant="subtitle2">Fahrzeugarten des Geräts</Typography>
              <Typography variant="caption" color="text.secondary">
                Bei Geräten mit Bildschirm wählt der Gast eine Fahrzeugart. Welche
                Kennung dabei übermittelt wird, legt Taxi Butler je Bestellort fest —
                tragen Sie sie hier ein und ordnen Sie Ihren Fahrzeugtyp zu.
              </Typography>
            </Box>
            {venueForm.carTypeRows.map((row, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Kennung"
                  value={row.identifier}
                  onChange={(e) =>
                    setVenueForm((f) => {
                      const rows = [...f.carTypeRows];
                      rows[index] = { ...rows[index], identifier: e.target.value };
                      return { ...f, carTypeRows: rows };
                    })
                  }
                  size="small"
                  placeholder="regular"
                  sx={{ flex: 1 }}
                />
                <TextField
                  select
                  label="Fahrzeugtyp"
                  value={row.vehicleType}
                  onChange={(e) =>
                    setVenueForm((f) => {
                      const rows = [...f.carTypeRows];
                      rows[index] = { ...rows[index], vehicleType: e.target.value };
                      return { ...f, carTypeRows: rows };
                    })
                  }
                  size="small"
                  sx={{ flex: 1 }}
                >
                  {VEHICLE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton
                  size="small"
                  onClick={() =>
                    setVenueForm((f) => ({
                      ...f,
                      carTypeRows: f.carTypeRows.filter((_, i) => i !== index)
                    }))
                  }
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setVenueForm((f) => ({
                  ...f,
                  carTypeRows: [...f.carTypeRows, { identifier: '', vehicleType: 1 }]
                }))
              }
            >
              Fahrzeugart ergänzen
            </Button>

            <FormControlLabel
              control={
                <Switch
                  checked={venueForm.active}
                  onChange={(e) => setVenueForm((f) => ({ ...f, active: e.target.checked }))}
                />
              }
              label="Bestellort aktiv"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVenueDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSaveVenue}
            disabled={
              busy ||
              !venueForm.name.trim() ||
              !venueForm.address.trim() ||
              venueForm.latitude === null
            }
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VenueChannelSetupGuidePage;
